import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { IconCloseSvg } from '../../../assets/inventory';
import { BlockedPlayer, listBlockedPlayers, unblockPlayer } from '../../../services/blocksApi';
import { showPlainToast } from '../../../utils/helpers/toastUtils';
import ChatAvatar from '../Chat/ChatAvatar';
import {
	CHAT_FONT as FONT,
	CHAT_LIST_FILL,
	CHAT_PANEL as PANEL,
	CHAT_PANEL_FILL,
	CHAT_PANEL_RIM,
	chatColors as colors,
} from '../Chat/chatTheme';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import RedesignModal from '../Modals/RedesignModal';
import { useRedesignScale } from '../Modals/useRedesignScale';

type Props = { onClose: () => void };

type State =
	| { status: 'loading' }
	| { status: 'failed' }
	| { status: 'loaded'; items: BlockedPlayer[] };

/**
 * The players you blocked, opened from your profile's BLOCK LIST. In the Message Center's chrome:
 * the red panel, a sunken list of rows with each player's avatar and name, and Unblock on each.
 * Unblocking takes the row away at once; it needs no confirmation, as on a profile.
 */
export const BlockListModal = ({ onClose }: Props) => {
	const { t } = useTranslation();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);
	const [state, setState] = useState<State>({ status: 'loading' });
	const [busyId, setBusyId] = useState<string | null>(null);

	const load = useCallback(() => {
		setState({ status: 'loading' });
		listBlockedPlayers()
			.then(items => setState({ status: 'loaded', items }))
			.catch(error => {
				console.warn('[BlockList] could not load', error?.message);
				setState({ status: 'failed' });
			});
	}, []);

	useEffect(load, [load]);

	const unblock = async (player: BlockedPlayer) => {
		setBusyId(player.id);
		try {
			await unblockPlayer(player.id);
			setState(current =>
				current.status === 'loaded'
					? { status: 'loaded', items: current.items.filter(item => item.id !== player.id) }
					: current,
			);
			showPlainToast(
				'success',
				t('T_SITE_BLOCK_UNBLOCK_DONE', { defaultValue: 'Player unblocked' }),
			);
		} catch {
			showPlainToast(
				'error',
				t('T_SITE_BLOCK_FAILED', { defaultValue: 'Something went wrong. Please try again.' }),
			);
		} finally {
			setBusyId(null);
		}
	};

	let status: React.ReactNode = null;
	if (state.status === 'loading') {
		status = <ActivityIndicator color={colors.gold300} />;
	} else if (state.status === 'failed') {
		status = (
			<View style={styles.statusStack}>
				<Text style={styles.status}>
					{t('T_SITE_BLOCK_LIST_FAILED', { defaultValue: 'Could not load your block list.' })}
				</Text>
				<Pressable accessibilityRole='button' onPress={load} style={styles.smallButton}>
					<Text style={styles.smallButtonLabel}>
						{t('T_SITE_CHAT_TRY_AGAIN', { defaultValue: 'Try again' })}
					</Text>
				</Pressable>
			</View>
		);
	} else if (!state.items.length) {
		status = (
			<Text style={styles.status}>
				{t('T_SITE_BLOCK_LIST_EMPTY', { defaultValue: "You haven't blocked anyone." })}
			</Text>
		);
	}

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				fill: CHAT_PANEL_FILL,
				rim: CHAT_PANEL_RIM,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				<View style={styles.titleBar}>
					<Text style={styles.title} numberOfLines={1}>
						{t('T_SITE_BLOCK_LIST', { defaultValue: 'Block list' }).toUpperCase()}
					</Text>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_CLOSE_BLOCK_LIST', {
							defaultValue: 'Close the block list',
						})}
						onPress={onClose}
						hitSlop={8}
						style={styles.close}
					>
						<IconCloseSvg width={24 * scale} height={24 * scale} />
					</Pressable>
				</View>

				<View style={styles.list}>
					<GradientFill stops={CHAT_LIST_FILL} />
					{status || state.status !== 'loaded' ? (
						<View style={styles.statusBox}>{status}</View>
					) : (
						<RedesignScrollView
							contentContainerStyle={styles.rows}
							indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
						>
							{state.items.map(player => (
								<View key={player.id} style={styles.row}>
									<GradientFill stops={CHAT_LIST_FILL} />
									<ChatAvatar avatarFileId={player.avatarFileId} size={44 * scale} scale={scale} />
									<Text style={styles.name} numberOfLines={1}>
										{player.username ?? ''}
									</Text>
									<Pressable
										accessibilityRole='button'
										accessibilityLabel={t('T_SITE_A11Y_UNBLOCK_PLAYER', {
											defaultValue: 'Unblock {{username}}',
											username: player.username ?? '',
										})}
										onPress={busyId ? undefined : () => unblock(player)}
										style={styles.smallButton}
									>
										{busyId === player.id ? (
											<ActivityIndicator color={colors.gold100} />
										) : (
											<Text style={styles.smallButtonLabel} numberOfLines={1}>
												{t('T_SITE_BLOCK_UNBLOCK', { defaultValue: 'Unblock' })}
											</Text>
										)}
									</Pressable>
								</View>
							))}
						</RedesignScrollView>
					)}
				</View>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const rim = Math.max(1, scale);
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(16),
		},
		titleBar: { flexDirection: 'row', alignItems: 'center', gap: s(8) },
		title: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(24),
			lineHeight: s(24 * 1.3),
			color: colors.gold200,
		},
		close: { width: s(32), height: s(32), alignItems: 'center', justifyContent: 'center' },
		list: {
			flex: 1,
			minHeight: 0,
			paddingHorizontal: s(6),
			paddingVertical: s(8),
			borderRadius: s(12),
			borderWidth: rim,
			borderColor: colors.listRim,
			overflow: 'hidden',
		},
		// 10 on the right keeps the rows clear of the scroll bar.
		rows: { gap: s(6), paddingTop: s(2), paddingRight: s(10), paddingBottom: s(2) },
		row: {
			minHeight: s(64),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(12),
			paddingHorizontal: s(10),
			paddingVertical: s(10),
			borderRadius: s(8),
			borderWidth: rim,
			borderColor: colors.rowBorder,
			overflow: 'hidden',
		},
		name: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(15),
			lineHeight: s(18),
			color: colors.gold50,
		},
		smallButton: {
			minWidth: s(84),
			height: s(32),
			paddingHorizontal: s(12),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(10),
			borderWidth: rim,
			borderColor: colors.buttonRim,
			backgroundColor: colors.mineFill,
		},
		smallButtonLabel: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(13),
			lineHeight: s(16),
			color: colors.gold100,
		},
		statusBox: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: s(16) },
		statusStack: { alignItems: 'center', gap: s(12) },
		status: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.muted,
			textAlign: 'center',
		},
	});
};

export default BlockListModal;
